<script setup lang="ts">
import { cloneDeep, isNil, isString } from "es-toolkit";
import type { FileUploadSelectEvent } from "primevue";
import FileUpload from "primevue/fileupload";
import { ref, toValue, watchEffect } from "vue";
import UpdateIcon from "~icons/ic/baseline-edit";
import ResetIcon from "~icons/prime/times";
import { formatUsageByte } from "../../common/formats.common";
import { defineActions, defineState } from "../common";
import { FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

interface FileMetadata {
    name: string;
    type: string;
    size: number;
}

type ModelType = string;
interface PropsType extends BaseProps<ModelType> {
    placeholder?: string;
    accept?: string;
    maxFileSize?: number;
    heightClass?: string;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    maxFileSize: 4 * 1024 * 1024,
    heightClass: undefined,
});

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const nameModel = defineModel<string | undefined>("name", {
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const originalName = ref<string>();
const actions = defineActions(model, props, state, {
    onCancel() {
        state.hasError = false;
        state.editMode = false;
        model.value = cloneDeep(toValue(state.original));
        nameModel.value = originalName.value;
    },
    onUpdate() {
        originalName.value = nameModel.value;
        state.original = cloneDeep(toValue(model.value)) as typeof state.original;
        state.editMode = true;
    },
});

watchEffect(() => {
    if (model.value === "" || model.value === null) {
        model.value = undefined;
    }
});

function onFileSelect(event: FileUploadSelectEvent) {
    const file = event.files[0] as FileMetadata;
    const reader = new FileReader();

    reader.onload = async (e) => {
        if (e.target === null) return;
        if (!isString(e.target.result)) return;
        model.value = e.target.result;
        nameModel.value = `${file.name} [${formatUsageByte(file.size)}]`;
    };

    reader.readAsDataURL(file as unknown as Blob);
}
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <div class="relative h-full rns-g-static-input">
                <div class="flex flex-col h-full min-h-50 max-h-100 items-center justify-center p-4">
                    <div class="h-full flex items-center justify-center">
                        {{ nameModel ?? props.label }}
                    </div>
                </div>
                <div v-if="state.updatable" class="rns-cover">
                    <div class="flex flex-col h-full items-center justify-center">
                        <div v-tooltip.top="'Ubah'">
                            <UpdateIcon class="text-gray-400 text-[3rem]" @click="actions.onUpdate" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="relative h-full rns-upload-box">
                <div
                    class="flex flex-col max-h-100 p-12 items-center justify-center"
                    :class="[isNil(props.heightClass) ? 'h-64' : props.heightClass]"
                >
                    <div v-if="state.hasValue">{{ nameModel }}</div>
                    <div v-else>
                        <FileUpload
                            :accept="props.accept"
                            auto
                            :chooseLabel="props.placeholder ?? 'Pilih Berkas'"
                            customUpload
                            :invalidFileSizeMessage="`Ukuran file tidak boleh melebihi ${formatUsageByte(props.maxFileSize)}.`"
                            :maxFileSize="props.maxFileSize"
                            mode="basic"
                            @select="onFileSelect"
                        />
                    </div>
                </div>
                <div v-if="state.hasValue" class="rns-cover rounded-md">
                    <div class="flex flex-col h-full items-center justify-center">
                        <div v-tooltip.top="'Reset'">
                            <ResetIcon class="text-gray-400 text-[4rem]" @click="actions.onReset" />
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-upload-box {
    @apply border border-gray-300 hover:border-gray-400 shadow-xs rounded-md;
    @apply max-h-100 w-full flex flex-row items-center justify-center hover:cursor-pointer;
}

.rns-cover {
    @apply absolute top-0 left-0 right-0 bottom-0;
    @apply hover:bg-black hover:cursor-pointer;
    @apply opacity-0 hover:opacity-60;
}
</style>
